"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SimpleFuturesProcessor = void 0;
const common_business_1 = require("@pionex-web-kit/common-business");
const lodash_1 = require("lodash");
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_quotation_lib_1 = require("trade_quotation_lib");
const trade_utils_lib_1 = require("trade_utils_lib");
const SimpleFuturesAPI_1 = require("../../../apis/SimpleFuturesAPI");
const Calculator_1 = require("../../../component/Calculator");
const FuturesConfig_1 = require("../../../component/FuturesConfig");
const context_1 = require("../../../context");
const hooks_1 = require("../../../hooks");
const interface_1 = require("../../../interface");
const errors_1 = require("../../../interface/errors");
const LeverageConfig_1 = require("../../FuturesConfig/FutureLeverageProvider/LeverageConfig");
const Trade_1 = require("../../Trade");
const SimpleFuturesTPSLProcessor_1 = require("../SimpleFuturesTPSLProcessor");
const utils_1 = require("../utils");
const useCurrentUserTradeHistory_1 = require("./useCurrentUserTradeHistory");
const usePositionLimit_1 = require("./usePositionLimit");
const SimpleFuturesLeverageKey = "SimpleFuturesLeverageKey";
const SimpleFuturesInvestKey = "SimpleFuturesInvestKey";
const SimpleFuturesReverseMargin = "SimpleFuturesReverseMargin";
/** 简易合约市价单模式的 TPSL local 设置的 key */
const SFTPSLMarketKey = "SimpleFuturesTPSLKeyV2";
const SimpleFuturesModeKey = "SimpleFuturesModeKey";
const SimpleFuturesTPSLModeKey = "SimpleFuturesTPSLModeKey";
const DefaultInvest = "10";
var SimpleFuturesProcessor;
(function (SimpleFuturesProcessor) {
    /** 交易页面需要的参数 */
    function useTradeUIParams(params) {
        const { symbolInfo, leverage, quoteBalance, isFocused = true, key_id, copyFrom, copyType, tpslParams, investInfo, triggerPrice, mode, investPrecision, predictSide, bonusId, } = params;
        const { invest, reverseMargin } = investInfo;
        const feeRate = (0, hooks_1.useFeeRate)(symbolInfo.base, symbolInfo.quote);
        const tradeLimit = useSymbolTradeQuoteLimit({
            symbolInfo,
            leverage,
            balance: quoteBalance,
            isActive: isFocused,
            triggerPrice,
            precision: investPrecision,
            side: predictSide,
            disabledMaxExtra: true, // 校验使用的最大值不要额外的冗余
        });
        const { min, max } = tradeLimit;
        const TPSLLimit = SimpleFuturesTPSLProcessor_1.SimpleFuturesTPSLProcessor.useOpenTPSLLimit({
            leverage: leverage,
            feeRate: feeRate,
            base: symbolInfo.base,
            quote: symbolInfo.quote,
            investInfo: investInfo,
            triggerPrice,
            tpslParams,
        });
        const TPSLState = SimpleFuturesTPSLProcessor_1.SimpleFuturesTPSLProcessor.useTPSLState(tpslParams, TPSLLimit);
        // 预期购买价值
        const volume = (0, react_1.useMemo)(() => {
            return new trade_utils_lib_1.SafeDecimal(invest).mul(leverage).toString();
        }, [invest, leverage]);
        // 市价开单的持仓均价
        const marketAvgPriceData = SimpleFuturesProcessor.useOpenOrderAvgPrice({ symbolInfo, volume }, isFocused);
        const avgPriceData = (0, react_1.useMemo)(() => {
            if (mode === interface_1.SimpleFutures.OrderMode.Market) {
                return marketAvgPriceData;
            }
            if (!triggerPrice) {
                return undefined;
            }
            return { buyAvgPrice: triggerPrice, sellAvgPrice: triggerPrice };
        }, [marketAvgPriceData, mode, triggerPrice]);
        const liquiPriceData = SimpleFuturesProcessor.useLiquidation({ symbolInfo, avgPriceData, investInfo, leverage, feeRate });
        const checkAndGetRequest = (0, react_1.useCallback)((params) => {
            const ob = (() => {
                const { side, disableTPSLCheck } = params;
                if (!key_id) {
                    return (0, rxjs_1.throwError)(new Error("Login status error"));
                }
                const { error: investInfoError } = SimpleFuturesProcessor.investErrorInfo({
                    investInfo,
                    available: quoteBalance !== null && quoteBalance !== void 0 ? quoteBalance : "",
                    quote: symbolInfo.quote,
                    investMin: min,
                    investMax: max,
                });
                if (investInfoError) {
                    return (0, rxjs_1.throwError)(() => investInfoError);
                }
                let tpsl = "";
                if (tpslParams) {
                    if (!disableTPSLCheck) {
                        const err = SimpleFuturesTPSLProcessor_1.SimpleFuturesTPSLProcessor.checkTPSLStateError(TPSLState, TPSLLimit);
                        if (err) {
                            return (0, rxjs_1.throwError)(err);
                        }
                    }
                    tpsl = SimpleFuturesTPSLProcessor_1.SimpleFuturesTPSLUtils.getAPITPSLParamsStr(tpslParams);
                }
                const request = {
                    key_id: key_id,
                    quote: symbolInfo.quote,
                    copy_type: copyType,
                    copy_from: copyFrom,
                    bu_order_data: {
                        quote_invest: invest,
                        extra_margin: reverseMargin,
                        investment_from: !!bonusId ? interface_1.SimpleFutures.InvestmentFrom.FUTURES_LITE_BONUS : undefined,
                        bonus_id: bonusId,
                        portfolio: [
                            {
                                base: symbolInfo.base,
                                leverage: leverage,
                                side: side,
                                mode,
                                price: triggerPrice,
                                tp_sl: tpsl,
                            },
                        ],
                    },
                };
                if (![interface_1.SimpleFutures.OrderMode.Trigger, interface_1.SimpleFutures.OrderMode.Limit].includes(mode)) {
                    // 做一个容错
                    request.bu_order_data.portfolio[0].price = undefined;
                }
                return (0, rxjs_1.of)(request);
            })();
            return ob.pipe((0, operators_1.catchError)((err) => {
                throw (0, trade_utils_lib_1.getTradeDependencyInstance)().localizeError(err);
            }));
        }, [
            TPSLLimit,
            TPSLState,
            bonusId,
            copyFrom,
            copyType,
            invest,
            investInfo,
            key_id,
            leverage,
            max,
            min,
            mode,
            quoteBalance,
            reverseMargin,
            symbolInfo.base,
            symbolInfo.quote,
            tpslParams,
            triggerPrice,
        ]);
        const openOrder = (0, react_1.useCallback)((params) => {
            return checkAndGetRequest(params).pipe((0, operators_1.mergeMap)((request) => {
                return SimpleFuturesAPI_1.SimpleFuturesAPI.createOrder(request);
            }), (0, operators_1.tap)((res) => {
                utils_1.SimpleFuturesOrderSubject.next(res);
            }));
        }, [checkAndGetRequest]);
        return {
            openOrder: openOrder,
            checkAndGetRequest: checkAndGetRequest,
            tradeLimit: tradeLimit,
            tpslState: TPSLState,
            tpslLimit: TPSLLimit,
            volume: volume,
            avgPriceData: avgPriceData,
            liquiPriceData: liquiPriceData,
        };
    }
    SimpleFuturesProcessor.useTradeUIParams = useTradeUIParams;
    /** 订阅市价预估开仓均价 volume为预期买入量 */
    const OBMaxLength = 150;
    const OBLengthPerTime = 50;
    function useOpenOrderAvgPrice(params, isActive) {
        const { symbolInfo, volume, side } = params;
        const [obLength, setLength] = (0, react_1.useState)(OBLengthPerTime);
        const [result, setResult] = (0, react_1.useState)();
        // 交易对变动时重置长度
        (0, react_1.useEffect)(() => {
            setLength(OBLengthPerTime);
        }, [symbolInfo.symbol, volume]);
        // 交易对变动时重置结果
        (0, react_1.useEffect)(() => {
            setResult(undefined);
        }, [symbolInfo.symbol]);
        (0, react_1.useEffect)(() => {
            if (!volume || !Number(volume) || !isActive) {
                return;
            }
            // 大于150档不取了
            if (obLength > OBMaxLength) {
                setResult(undefined);
                return;
            }
            const sub = (0, trade_quotation_lib_1.getGlobalQuotationSocketMgr)()
                .getOrderBookV2WebSocket()
                .getOrderBookObservable({
                market: "pionex.v2",
                base: symbolInfo.base,
                quote: symbolInfo.quote,
                limit: Math.min(OBMaxLength, obLength),
            })
                .pipe((0, operators_1.throttleTime)(1000), (0, operators_1.filter)((data) => {
                // 检测长度是否足够
                if (!side || side === interface_1.CustomOrderSide.BUY) {
                    // 买的话要吃卖盘
                    const asks = data.asks;
                    // 取ob中最大成交额
                    if (asks.length) {
                        const asksVolumeSum = asks[asks.length - 1].accVolume;
                        if (asksVolumeSum < Number(volume)) {
                            setLength((prevState) => {
                                return prevState + OBLengthPerTime;
                            });
                            return false;
                        }
                    }
                }
                if (!side || side === interface_1.CustomOrderSide.SELL) {
                    // 卖的话要吃买盘
                    const bids = data.bids;
                    // 取ob中最大成交额
                    if (bids.length) {
                        const bidsVolumeSum = bids[bids.length - 1].accVolume;
                        if (bidsVolumeSum < Number(volume)) {
                            setLength((prevState) => {
                                return prevState + OBLengthPerTime;
                            });
                            return false;
                        }
                    }
                }
                return true;
            }), (0, operators_1.map)((data) => {
                let asksAmountSum = 0;
                let asksVolumeSum = 0;
                let bidsAmountSum = 0;
                let bidsVolumeSum = 0;
                // 检测长度是否足够
                if (!side || side === interface_1.CustomOrderSide.BUY) {
                    // 买的话要吃卖盘
                    const asks = data.asks;
                    for (let i = 0; i < asks.length; i++) {
                        const item = asks[i];
                        // 还剩多少投资额没加
                        const remainderValue = Number(volume) - asksVolumeSum;
                        if (remainderValue <= 0) {
                            break;
                        }
                        // 如果剩余没加的交易额大于该档的交易额，直接赋值从卖1到该档的总交易额
                        // 反之,加剩余的 结束遍历
                        if (remainderValue > item.volume) {
                            asksVolumeSum = item.accVolume;
                            asksAmountSum = item.accQuantity;
                        }
                        else {
                            asksVolumeSum = asksVolumeSum + remainderValue;
                            asksAmountSum = asksAmountSum + (remainderValue / item.volume) * item.quantity;
                            break;
                        }
                    }
                }
                if (!side || side === interface_1.CustomOrderSide.SELL) {
                    // 卖的话要吃买盘
                    const bids = data.bids;
                    for (let i = 0; i < bids.length; i++) {
                        const item = bids[i];
                        // 还剩多少投资额没加
                        const remainderValue = Number(volume) - bidsVolumeSum;
                        if (remainderValue <= 0) {
                            break;
                        }
                        // 如果剩余没加的交易额大于该档的交易额，直接赋值从卖1到该档的总交易额
                        // 反之,加剩余的 结束遍历
                        if (remainderValue > item.volume) {
                            bidsVolumeSum = item.accVolume;
                            bidsAmountSum = item.accQuantity;
                        }
                        else {
                            bidsVolumeSum = bidsVolumeSum + remainderValue;
                            bidsAmountSum = bidsAmountSum + (remainderValue / item.volume) * item.quantity;
                            break;
                        }
                    }
                }
                const buyAvgPrice = asksAmountSum ? asksVolumeSum / asksAmountSum : 0;
                const sellAvgPrice = bidsAmountSum ? bidsVolumeSum / bidsAmountSum : 0;
                return {
                    buyAvgPrice: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(buyAvgPrice, symbolInfo.precision, trade_utils_lib_1.SafeDecimal.ROUND_CEIL),
                    sellAvgPrice: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(sellAvgPrice, symbolInfo.precision),
                };
            }))
                .subscribe({
                next: setResult,
                error: (err) => {
                    console.log(`Error: useOpenOrderAvgPrice: ${err} `);
                },
            });
            return () => {
                sub.unsubscribe();
            };
        }, [isActive, obLength, side, symbolInfo.base, symbolInfo.precision, symbolInfo.quote, volume]);
        return result;
    }
    SimpleFuturesProcessor.useOpenOrderAvgPrice = useOpenOrderAvgPrice;
    /** 关单 */
    function closeOrder(id) {
        return SimpleFuturesAPI_1.SimpleFuturesAPI.closeOrder({ bu_order_id: id, mode: "market" });
    }
    SimpleFuturesProcessor.closeOrder = closeOrder;
    /** 关单所有的订单 */
    function closeAllOrder(data) {
        return SimpleFuturesAPI_1.SimpleFuturesAPI.closeAllOrder(data);
    }
    SimpleFuturesProcessor.closeAllOrder = closeAllOrder;
    /**
     * 获取当前场景的最大最小投资额
     * @param symbolInfo
     * @param leverage 当前杠杆倍数，如果没有传，就取币对的最大杠杆倍数
     * @param balance
     * @param isActive
     * @param triggerPrice
     * @param outPrecision 外部可以强制指定小数位
     * @param side
     * @param disabledMaxExtra 忽略计算最大值时的额外冗余
     */
    function useSymbolTradeQuoteLimit({ symbolInfo, leverage, balance = "", isActive = true, triggerPrice, precision: outPrecision, side, disabledMaxExtra, }) {
        var _a, _b;
        const precision = outPrecision !== null && outPrecision !== void 0 ? outPrecision : symbolInfo.precisionCost;
        const indexTicker = (0, context_1.useFuturesIndexParseContext)({
            base: symbolInfo.base,
            quote: symbolInfo.quote,
            isFocused: isActive && !triggerPrice,
        });
        const priceD = (0, react_1.useMemo)(() => new trade_utils_lib_1.SafeDecimal(triggerPrice || (indexTicker === null || indexTicker === void 0 ? void 0 : indexTicker.mark_price)), [indexTicker === null || indexTicker === void 0 ? void 0 : indexTicker.mark_price, triggerPrice]);
        const leverageConfig = (0, FuturesConfig_1.useLeverageConfigRow)(symbolInfo.symbol, leverage);
        const baseLimit = (_a = symbolInfo.limit) === null || _a === void 0 ? void 0 : _a.dumping;
        const quoteLimit = (_b = symbolInfo.limit) === null || _b === void 0 ? void 0 : _b.spending;
        const tpslConfig = (0, Trade_1.useFuturesTPSLConfig)(symbolInfo.symbol);
        return (0, react_1.useMemo)(() => {
            if (leverage === undefined && leverageConfig.realMaxLeverage === undefined) {
                return {
                    min: undefined,
                    max: undefined,
                };
            }
            /** 真实使用的杠杆倍数 */
            const usedLeverage = leverage !== undefined ? leverage : leverageConfig.realMaxLeverage;
            let max = undefined;
            if (baseLimit === null || baseLimit === void 0 ? void 0 : baseLimit.max) {
                // 包含了防冲击逻辑的价格
                const maxPriceWithImpact = (() => {
                    if (!triggerPrice) {
                        // 只有市价单做防冲击换算
                        return utils_1.SimpleFuturesUtils.calcMaxImpactMarketPrice({
                            symbolInfo,
                            orderSide: side || interface_1.CustomOrderSide.SELL,
                            markPrice: indexTicker === null || indexTicker === void 0 ? void 0 : indexTicker.mark_price,
                        });
                    }
                    return new trade_utils_lib_1.SafeDecimal(triggerPrice);
                })();
                // 额外的冗余
                const extra = disabledMaxExtra ? new trade_utils_lib_1.SafeDecimal(1) : new trade_utils_lib_1.SafeDecimal(1).sub(new trade_utils_lib_1.SafeDecimal(tpslConfig === null || tpslConfig === void 0 ? void 0 : tpslConfig.offsetLimit));
                max = new trade_utils_lib_1.SafeDecimal(baseLimit.max).mul(maxPriceWithImpact).mul(extra).div(usedLeverage).toString();
                if (quoteLimit === null || quoteLimit === void 0 ? void 0 : quoteLimit.max) {
                    max = trade_utils_lib_1.SafeDecimal.min(max, new trade_utils_lib_1.SafeDecimal(quoteLimit.max).div(usedLeverage)).toString();
                }
            }
            if (leverageConfig.curLeverageRow) {
                if (max) {
                    max = trade_utils_lib_1.SafeDecimal.min(new trade_utils_lib_1.SafeDecimal(max), new trade_utils_lib_1.SafeDecimal(leverageConfig.curLeverageRow.notional_limit).div(usedLeverage)).toString();
                }
                else {
                    max = leverageConfig.curLeverageRow.notional_limit;
                }
            }
            if (balance) {
                if (max) {
                    max = trade_utils_lib_1.SafeDecimal.min(new trade_utils_lib_1.SafeDecimal(max), new trade_utils_lib_1.SafeDecimal(balance)).toString();
                }
                else {
                    max = balance;
                }
            }
            let min = undefined;
            if (baseLimit === null || baseLimit === void 0 ? void 0 : baseLimit.min) {
                // base 的最小，乘以2来做冗余
                min = new trade_utils_lib_1.SafeDecimal(baseLimit.min).mul(2).mul(priceD).div(usedLeverage).toString();
                if (quoteLimit === null || quoteLimit === void 0 ? void 0 : quoteLimit.min) {
                    min = trade_utils_lib_1.SafeDecimal.max(min, new trade_utils_lib_1.SafeDecimal(quoteLimit.min).div(usedLeverage)).toString();
                }
            }
            return {
                min: min ? trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(min, precision, trade_utils_lib_1.SafeDecimal.ROUND_CEIL) : undefined,
                max: max ? trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(max, precision, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR) : undefined,
            };
        }, [
            leverage,
            leverageConfig.realMaxLeverage,
            leverageConfig.curLeverageRow,
            baseLimit === null || baseLimit === void 0 ? void 0 : baseLimit.max,
            baseLimit === null || baseLimit === void 0 ? void 0 : baseLimit.min,
            balance,
            precision,
            disabledMaxExtra,
            tpslConfig === null || tpslConfig === void 0 ? void 0 : tpslConfig.offsetLimit,
            quoteLimit === null || quoteLimit === void 0 ? void 0 : quoteLimit.max,
            quoteLimit === null || quoteLimit === void 0 ? void 0 : quoteLimit.min,
            triggerPrice,
            symbolInfo,
            side,
            indexTicker === null || indexTicker === void 0 ? void 0 : indexTicker.mark_price,
            priceD,
        ]);
    }
    SimpleFuturesProcessor.useSymbolTradeQuoteLimit = useSymbolTradeQuoteLimit;
    // 计算强平价
    function useLiquidation(params) {
        var _a;
        const { symbolInfo, avgPriceData, investInfo, leverage, feeRate } = params;
        const configData = (0, FuturesConfig_1.useLeverageConfig)(symbolInfo.symbol);
        return (0, react_1.useMemo)(() => {
            var _a, _b, _c, _d;
            if (!avgPriceData) {
                return;
            }
            // 杠杆倍数
            const leverageD = new trade_utils_lib_1.SafeDecimal(leverage);
            // 多仓开仓均价
            const longAvg = new trade_utils_lib_1.SafeDecimal(avgPriceData.buyAvgPrice);
            // 空仓开仓均价
            const shortAvg = new trade_utils_lib_1.SafeDecimal(avgPriceData.sellAvgPrice);
            // 投资额
            const investD = new trade_utils_lib_1.SafeDecimal(investInfo.invest);
            // 仓位价值
            const positionValue = investD.mul(leverageD);
            /** 预计多仓仓位数量 */
            const longQty = positionValue.div(longAvg);
            /** 处理过精度的预计多仓仓位数量 */
            const formattedLongQty = trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(longQty, (_a = symbolInfo.amount) !== null && _a !== void 0 ? _a : 0, trade_utils_lib_1.SafeDecimal.ROUND_DOWN);
            /** 预计空仓仓位数量 */
            const shortQty = positionValue.div(shortAvg);
            /** 处理过精度的预计空仓仓位数量 */
            const formattedShortQty = trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(shortQty, (_b = symbolInfo.amount) !== null && _b !== void 0 ? _b : 0, trade_utils_lib_1.SafeDecimal.ROUND_DOWN);
            /** 多仓预估交易量 */
            const longEstVolume = new trade_utils_lib_1.SafeDecimal(formattedLongQty).mul(longAvg);
            const shortEstVolume = new trade_utils_lib_1.SafeDecimal(formattedShortQty).mul(shortAvg);
            const assetsLong = new trade_utils_lib_1.SafeDecimal(formattedLongQty).mul(longAvg).div(leverageD);
            const assetsShort = new trade_utils_lib_1.SafeDecimal(formattedShortQty).mul(shortAvg).div(leverageD);
            const longLiquiPrice = Calculator_1.LiquidationCalculator.calcuLiquidationPrice({
                leverageConfigs: ((_c = configData.leverageConfig) === null || _c === void 0 ? void 0 : _c.rows) || [],
                position: formattedLongQty,
                avgPrice: longAvg.toString(),
                side: "long",
                assets: assetsLong.toString(),
                otherSpending: longEstVolume.mul(new trade_utils_lib_1.SafeDecimal(feeRate === null || feeRate === void 0 ? void 0 : feeRate.takerFeeRate)),
            });
            const shortLiquiPrice = Calculator_1.LiquidationCalculator.calcuLiquidationPrice({
                leverageConfigs: ((_d = configData.leverageConfig) === null || _d === void 0 ? void 0 : _d.rows) || [],
                position: formattedShortQty,
                avgPrice: shortAvg.toString(),
                side: "short",
                assets: assetsShort.toString(),
                otherSpending: shortEstVolume.mul(new trade_utils_lib_1.SafeDecimal(feeRate === null || feeRate === void 0 ? void 0 : feeRate.takerFeeRate)),
            });
            return {
                longLiquiPrice: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(longLiquiPrice, symbolInfo.precision, trade_utils_lib_1.SafeDecimal.ROUND_CEIL),
                shortLiquiPrice: trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(shortLiquiPrice, symbolInfo.precision, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR),
            };
        }, [
            avgPriceData,
            (_a = configData.leverageConfig) === null || _a === void 0 ? void 0 : _a.rows,
            feeRate === null || feeRate === void 0 ? void 0 : feeRate.takerFeeRate,
            investInfo.invest,
            leverage,
            symbolInfo.amount,
            symbolInfo.precision,
        ]);
    }
    SimpleFuturesProcessor.useLiquidation = useLiquidation;
    /** 设置默认的杠杆倍数 */
    function setLocalDefaultLeverage(value) {
        (0, trade_utils_lib_1.getLocalStorage)().set(SimpleFuturesLeverageKey, `${value}`);
    }
    SimpleFuturesProcessor.setLocalDefaultLeverage = setLocalDefaultLeverage;
    /**
     * 获取当前配置的杠杆倍数，在还没有获取到的时候返回默认值：DefaultMaxLeverage
     * @param {ExchangeSymbol} symbolInfo 币对信息
     * @param {(validLeverage: number) => void} onLeverageExceeded 初始化币对、切换币对两种情况都会检查本地存储的杠杆倍数和当前币对的杠杆倍数，如果本地大于 symbol 的话就会调用此函数
     */
    SimpleFuturesProcessor.useLeverage = (symbolInfo, onLeverageExceeded) => {
        const [localLeverage, setLocalLeverage] = (0, react_1.useState)();
        // 1. 获取本地存储的杠杆倍数
        (0, react_1.useEffect)(() => {
            (0, trade_utils_lib_1.getLocalStorage)()
                .get(SimpleFuturesLeverageKey, LeverageConfig_1.DefaultMaxLeverage.toString())
                .pipe((0, operators_1.map)((res) => new trade_utils_lib_1.SafeDecimal(res).toNumber()))
                .subscribe({
                next: (val) => {
                    setLocalLeverage(val);
                },
                error: () => { },
            });
        }, []);
        // 2. 获取当前币对杠杆配置信息
        const symbolLeverageConfig = (0, FuturesConfig_1.useLeverageConfigRow)(symbolInfo.symbol);
        // 检查本地杠杆倍数是否超出 symbol 的杠杆倍数
        // （包含初始化、切换币对，两种情况的检查）
        (0, common_business_1.useUpdateEffect)(() => {
            if (!onLeverageExceeded || localLeverage === undefined || symbolLeverageConfig.realMaxLeverage === undefined) {
                return;
            }
            if (localLeverage > symbolLeverageConfig.realMaxLeverage) {
                onLeverageExceeded(symbolLeverageConfig.realMaxLeverage);
            }
        }, [localLeverage, symbolLeverageConfig.realMaxLeverage]);
        /** 对外暴露的杠杆倍数（没有获取到数据情况，返回 DefaultMaxLeverage） */
        const leverage = (0, react_1.useMemo)(() => {
            if (!localLeverage || (symbolLeverageConfig === null || symbolLeverageConfig === void 0 ? void 0 : symbolLeverageConfig.realMaxLeverage) === undefined) {
                return LeverageConfig_1.DefaultMaxLeverage;
            }
            if (localLeverage > (symbolLeverageConfig === null || symbolLeverageConfig === void 0 ? void 0 : symbolLeverageConfig.realMaxLeverage)) {
                return symbolLeverageConfig === null || symbolLeverageConfig === void 0 ? void 0 : symbolLeverageConfig.realMaxLeverage;
            }
            return localLeverage;
        }, [localLeverage, symbolLeverageConfig === null || symbolLeverageConfig === void 0 ? void 0 : symbolLeverageConfig.realMaxLeverage]);
        const setLeverage = (0, react_1.useCallback)((val) => {
            setLocalLeverage(val);
            SimpleFuturesProcessor.setLocalDefaultLeverage(val);
        }, []);
        return [leverage, setLeverage];
    };
    /** 设置默认的投资额 */
    function setLocalDefaultInvest(value) {
        (0, trade_utils_lib_1.getLocalStorage)().set(SimpleFuturesInvestKey, value);
    }
    SimpleFuturesProcessor.setLocalDefaultInvest = setLocalDefaultInvest;
    /**
     * 设置默认的预留保证金金额
     * @param value
     */
    function setLocalDefaultReverseMargin(value) {
        (0, trade_utils_lib_1.getLocalStorage)().set(SimpleFuturesReverseMargin, value);
    }
    SimpleFuturesProcessor.setLocalDefaultReverseMargin = setLocalDefaultReverseMargin;
    /**
     * 获取当前默认的投资额信息
     */
    function useInvestInfo(options) {
        const { defaultInvest = DefaultInvest } = options || {};
        const [investInfo, setInvestInfo] = (0, react_1.useState)({ invest: defaultInvest });
        // 初始化杠杆倍数
        (0, react_1.useEffect)(() => {
            (0, rxjs_1.forkJoin)([
                (0, trade_utils_lib_1.getLocalStorage)().get(SimpleFuturesInvestKey, defaultInvest),
                (0, trade_utils_lib_1.getLocalStorage)().get(SimpleFuturesReverseMargin),
            ]).subscribe({
                next: (values) => {
                    var _a, _b;
                    setInvestInfo({ invest: (_a = values[0]) !== null && _a !== void 0 ? _a : defaultInvest, reverseMargin: (_b = values[1]) !== null && _b !== void 0 ? _b : undefined });
                },
                error: () => { },
            });
        }, [defaultInvest]);
        return [investInfo, setInvestInfo];
    }
    SimpleFuturesProcessor.useInvestInfo = useInvestInfo;
    // TPSL 默认的模式
    const defaultTPMode = interface_1.SimpleFutures.TPSLMode.simple_price;
    const defaultSLMode = interface_1.SimpleFutures.TPSLMode.simple_price;
    /**
     * 获取默认的 TPSL 的值
     * @param isSFMarketMode 是否是简易合约的市价单模式
     */
    function getDefaultTPSLParams(isSFMarketMode) {
        // NOTE: 简易合约的市价单模式，只支持盈亏率、追踪出场两种 TPSL，现在默认选择盈亏率
        if (isSFMarketMode) {
            return {
                profit: { mode: interface_1.SimpleFutures.TPSLMode.simple_rate },
                loss: { mode: interface_1.SimpleFutures.TPSLMode.simple_rate },
            };
        }
        return {
            profit: { mode: defaultTPMode },
            loss: { mode: defaultSLMode },
        };
    }
    /**
     * 把 pro 的合约 TPSL 类型转换为简易合约的 TPSL 类型
     */
    const transferProTPSLInputMode = (val) => {
        switch (val) {
            case interface_1.SimpleFutures.ProTradeStorageTPSLInputMode.percent:
                return interface_1.SimpleFutures.TPSLMode.simple_rate;
            case interface_1.SimpleFutures.ProTradeStorageTPSLInputMode.pnl:
                return interface_1.SimpleFutures.TPSLMode.simple_amount;
            case interface_1.SimpleFutures.ProTradeStorageTPSLInputMode.price:
                return interface_1.SimpleFutures.TPSLMode.simple_price;
            default:
                return defaultTPMode;
        }
    };
    /**
     * 把简易合约 TPSL 类型转换为 pro 合约的 TPSL 类型
     */
    const transferSFTPSLInputMode = (val) => {
        switch (val) {
            case interface_1.SimpleFutures.TPSLMode.simple_rate:
                return interface_1.SimpleFutures.ProTradeStorageTPSLInputMode.percent;
            case interface_1.SimpleFutures.TPSLMode.simple_amount:
                return interface_1.SimpleFutures.ProTradeStorageTPSLInputMode.pnl;
            case interface_1.SimpleFutures.TPSLMode.simple_price:
                return interface_1.SimpleFutures.ProTradeStorageTPSLInputMode.price;
            default:
                return interface_1.SimpleFutures.ProTradeStorageTPSLInputMode.price;
        }
    };
    /**
     * 把 pro 的合约交易存储数据转换为简易合约的 TPSL 数据
     */
    const transferProTPSL = (proTradeParams) => {
        return {
            profit: {
                mode: transferProTPSLInputMode(proTradeParams === null || proTradeParams === void 0 ? void 0 : proTradeParams.tpInputMode),
            },
            loss: {
                mode: transferProTPSLInputMode(proTradeParams === null || proTradeParams === void 0 ? void 0 : proTradeParams.slInputMode),
            },
        };
    };
    /** 获取本地的 TPSL 设置的值 */
    const getLocalTPSL = (isSFMarketMode) => {
        const storageKey = isSFMarketMode ? SFTPSLMarketKey : SimpleFuturesProcessor.FUTURES_TRADING_STORAGE;
        return (0, trade_utils_lib_1.getLocalStorage)()
            .get(storageKey)
            .pipe((0, operators_1.map)((res) => {
            if (!isSFMarketMode && res) {
                const futuresTradeStorage = JSON.parse(res);
                return transferProTPSL(futuresTradeStorage);
            }
            return res ? JSON.parse(res) : getDefaultTPSLParams(isSFMarketMode);
        }));
    };
    /** 合约交易相关存储的 key */
    SimpleFuturesProcessor.FUTURES_TRADING_STORAGE = "KEY_FUTURES_TRADING_STORAGE";
    /**
     * 简易合约、分仓、行情 TPSL 存储时候通知的事件流
     * （注：简易合约的市价单模式修改不会发送此事件）
     */
    SimpleFuturesProcessor.tpslTypeStorage$ = new rxjs_1.Subject();
    /**
     * 禁止存储在本地的 TPSL 类型
     * （注：追踪（simple_trailing）模式只在分仓有，全/逐仓在开仓的时候没有，所以是禁止存储选项）
     */
    const disableCachedTPSLModes = [interface_1.SimpleFutures.TPSLMode.simple_trailing];
    /** 是否是禁止存储在本地的 TPSL 类型 */
    const isDisableCachedTPSLMode = (mode) => {
        return mode ? disableCachedTPSLModes.includes(mode) : false;
    };
    /**
     * 缓存开仓时的 TPSL 的值（isSFMarketMode 模式以外都只缓 mode，不缓存具体的值）
     * 注：如果是禁止存储的模式（比如追踪模式），会忽略且不会存储到本地
     */
    function cacheTPSLOnOpen(options) {
        if (options.isSFMarketMode) {
            (0, trade_utils_lib_1.getLocalStorage)().set(SFTPSLMarketKey, JSON.stringify(options.value));
        }
        else {
            // 优先从缓存读取 pro 那边的设置，然后再修改设置
            (0, trade_utils_lib_1.getLocalStorage)()
                .get(SimpleFuturesProcessor.FUTURES_TRADING_STORAGE)
                .subscribe({
                next: (oldData) => {
                    var _a, _b, _c, _d;
                    const newData = oldData ? JSON.parse(oldData) : {};
                    let isChanged = false;
                    if (!isDisableCachedTPSLMode((_a = options.value.profit) === null || _a === void 0 ? void 0 : _a.mode)) {
                        isChanged = true;
                        newData.tpInputMode = transferSFTPSLInputMode((_b = options.value.profit) === null || _b === void 0 ? void 0 : _b.mode);
                    }
                    if (!isDisableCachedTPSLMode((_c = options.value.loss) === null || _c === void 0 ? void 0 : _c.mode)) {
                        isChanged = true;
                        newData.slInputMode = transferSFTPSLInputMode((_d = options.value.loss) === null || _d === void 0 ? void 0 : _d.mode);
                    }
                    if (isChanged) {
                        SimpleFuturesProcessor.tpslTypeStorage$.next({
                            data: newData,
                            eventId: options.eventId,
                        });
                        (0, trade_utils_lib_1.getLocalStorage)().set(SimpleFuturesProcessor.FUTURES_TRADING_STORAGE, JSON.stringify(newData));
                    }
                },
                error: () => { },
            });
        }
    }
    SimpleFuturesProcessor.cacheTPSLOnOpen = cacheTPSLOnOpen;
    /**
     * 开仓时的 TPSL 设置
     * @param options.isSFMarketMode?: boolean
     */
    function useTPSLOnOpen(options) {
        /** 当前 hooks 在使用时的唯一 ID */
        const hooksId = (0, react_1.useRef)((0, lodash_1.uniqueId)());
        const { isSFMarketMode } = options || {};
        const [TPSL, setOriginTPSL] = (0, react_1.useState)(() => getDefaultTPSLParams(isSFMarketMode));
        /**
         * 设置 hooks TPSL 的值（setOriginTPSL），并且会同步缓存到本地（localStorage）；并且会发出 rx 事件通知其他使用这个 hooks 的地方更新这个值
         * 注：如果是追踪模式的话，是不会存储到本地的（因为追踪模式只在分仓有，全/逐仓在开仓的时候没有）
         */
        const setTPSL = (0, react_1.useCallback)((value) => {
            setOriginTPSL((oldVal) => {
                const newVal = Object.assign(Object.assign({}, oldVal), value);
                // 设置缓存
                SimpleFuturesProcessor.cacheTPSLOnOpen({
                    value: newVal,
                    isSFMarketMode,
                    eventId: hooksId.current,
                });
                return newVal;
            });
        }, [isSFMarketMode]);
        // 初始化获取本地
        (0, react_1.useEffect)(() => {
            const subscription = getLocalTPSL(isSFMarketMode).subscribe({
                next: (val) => {
                    setOriginTPSL(val);
                },
                error: () => { },
            });
            return () => {
                subscription.unsubscribe();
            };
        }, [isSFMarketMode]);
        // 监听本地 TPSL 模式改变
        (0, react_1.useEffect)(() => {
            const subscription = SimpleFuturesProcessor.tpslTypeStorage$.subscribe({
                next: (val) => {
                    if (val.eventId !== hooksId.current) {
                        setOriginTPSL((oldVal) => {
                            var _a, _b, _c, _d;
                            const newVal = (0, lodash_1.cloneDeep)(oldVal);
                            const transferNewLocalValue = transferProTPSL(val.data);
                            if (((_a = newVal.profit) === null || _a === void 0 ? void 0 : _a.mode) !== ((_b = transferNewLocalValue.profit) === null || _b === void 0 ? void 0 : _b.mode)) {
                                newVal.profit = transferNewLocalValue.profit;
                            }
                            if (((_c = newVal.loss) === null || _c === void 0 ? void 0 : _c.mode) !== ((_d = transferNewLocalValue.loss) === null || _d === void 0 ? void 0 : _d.mode)) {
                                newVal.loss = transferNewLocalValue.loss;
                            }
                            return newVal;
                        });
                    }
                },
                error: () => { },
            });
            return () => {
                subscription.unsubscribe();
            };
        }, []);
        /** 清空 tpsl 输入的 值 */
        const clearTPSLValue = (0, react_1.useCallback)(() => {
            setOriginTPSL((val) => {
                var _a, _b;
                return {
                    profit: {
                        mode: ((_a = val.profit) === null || _a === void 0 ? void 0 : _a.mode) || defaultTPMode,
                    },
                    loss: {
                        mode: ((_b = val.loss) === null || _b === void 0 ? void 0 : _b.mode) || defaultTPMode,
                    },
                };
            });
        }, []);
        return { TPSL, setTPSL, clearTPSLValue };
    }
    SimpleFuturesProcessor.useTPSLOnOpen = useTPSLOnOpen;
    function setLocalDefaultMode(mode) {
        (0, trade_utils_lib_1.getLocalStorage)().set(SimpleFuturesModeKey, mode);
    }
    SimpleFuturesProcessor.setLocalDefaultMode = setLocalDefaultMode;
    function useMode(init) {
        const [mode, setMode] = (0, react_1.useState)(init);
        (0, react_1.useEffect)(() => {
            (0, trade_utils_lib_1.getLocalStorage)()
                .get(SimpleFuturesModeKey, interface_1.SimpleFutures.OrderMode.Market)
                .subscribe({ next: (v) => setMode(v), error: () => { } });
        }, []);
        return [mode, setMode];
    }
    SimpleFuturesProcessor.useMode = useMode;
    function setLocaleDefaultTPSLMode(type, tpslMode) {
        (0, trade_utils_lib_1.getLocalStorage)().set(`${SimpleFuturesTPSLModeKey}_${type}`, tpslMode);
    }
    SimpleFuturesProcessor.setLocaleDefaultTPSLMode = setLocaleDefaultTPSLMode;
    function useTPSLMode(type, initialMode) {
        const [mode, setMode] = (0, react_1.useState)(initialMode !== null && initialMode !== void 0 ? initialMode : interface_1.SimpleFutures.TPSLMode.simple_rate);
        (0, react_1.useLayoutEffect)(() => {
            if (initialMode) {
                // 有初始值就不需要获取缓存
                return;
            }
            (0, trade_utils_lib_1.getLocalStorage)()
                .get(`${SimpleFuturesTPSLModeKey}_${type}`)
                .subscribe({
                next: (value) => {
                    if (!value) {
                        return;
                    }
                    setMode(value);
                },
                error: () => { },
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);
        return [mode, setMode];
    }
    SimpleFuturesProcessor.useTPSLMode = useTPSLMode;
    /** 充值/提取保证金 */
    SimpleFuturesProcessor.transferMargin = (params) => {
        return SimpleFuturesAPI_1.SimpleFuturesAPI.transferProfit(Object.assign(Object.assign({}, params), { type: "margin" }));
    };
    /** 异步获取开仓限额的名义价值 */
    SimpleFuturesProcessor.usePositionLimit = usePositionLimit_1.usePositionLimit;
    /** 当前用户在指定时间范围内的交易数据 */
    SimpleFuturesProcessor.useCurrentUserTrades = useCurrentUserTradeHistory_1.useCurrentUserTrades;
    SimpleFuturesProcessor.useCurrentUserTradesSubject = useCurrentUserTradeHistory_1.useCurrentUserTradesSubject;
    /**
     * 投资额/本金相关的错误
     * @param params
     */
    SimpleFuturesProcessor.investErrorInfo = (params) => {
        const { investInfo, available, investMax, investMin, quote, pureInvestError } = params;
        let investError;
        let balanceError;
        const investD = new trade_utils_lib_1.SafeDecimal(investInfo.invest);
        const principal = new trade_utils_lib_1.SafeDecimal(investInfo.reverseMargin).add(new trade_utils_lib_1.SafeDecimal(investInfo.invest));
        const minD = new trade_utils_lib_1.SafeDecimal(investMin);
        const maxD = new trade_utils_lib_1.SafeDecimal(investMax);
        /** 余额最小值： max(币对最小投资额, 用户输入的真实的投资额) */
        const minimumAvailable = trade_utils_lib_1.SafeDecimal.max(minD, principal);
        // 余额不足独立判断
        if (minimumAvailable.greaterThan(available)) {
            balanceError = (0, errors_1.cloneError)(errors_1.SF_BALANCE_NOT_ENOUGH);
        }
        if (investMin && investD.lessThan(minD)) {
            // 投资额小于最小投资额的时候，报红需要加上 extra margin
            investError = (0, errors_1.cloneError)(errors_1.SF_INVEST_GREATER_THAN, {
                min: pureInvestError ? investMin : new trade_utils_lib_1.SafeDecimal(investMin).add(new trade_utils_lib_1.SafeDecimal(investInfo.reverseMargin)).toString(),
                quote,
            });
        }
        else if (investMax && investD.greaterThan(maxD)) {
            investError = (0, errors_1.cloneError)(errors_1.SF_INVEST_LESS_THAN, { max: investMax, quote });
        }
        const error = balanceError !== null && balanceError !== void 0 ? balanceError : investError;
        const isValid = !balanceError && !investError;
        return { isValid, error, investError, balanceError };
    };
    SimpleFuturesProcessor.cancelTriggerOrder = SimpleFuturesAPI_1.SimpleFuturesAPI.cancelTriggerOrder;
    SimpleFuturesProcessor.cancelLimitOrder = SimpleFuturesAPI_1.SimpleFuturesAPI.cancelLimitOrder;
})(SimpleFuturesProcessor = exports.SimpleFuturesProcessor || (exports.SimpleFuturesProcessor = {}));
